import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Bot,
  List,
  Maximize2,
  MessageSquarePlus,
  PictureInPicture2,
  X,
} from "lucide-react";
import { useAronConversationInfo } from "../api/aronApi";
import { useAronChat } from "../hooks/useAronChat";
import { useAronTabSignals } from "../hooks/useAronTabSignals";
import { useAronUiStore } from "../store/aronUiStore";
import { AronConversationList } from "./AronConversationList";
import { AronChatView } from "./AronChatView";

/**
 * Content of the detached ARON dock — a self-contained chat (its own selected
 * conversation lives in the store, independent of the page URL) plus a compact
 * header. The resizable shell + drag handle live in AronDockHost; this renders
 * inside it. Reuses the same orchestration hook and message/composer views as
 * the full page, so behaviour stays identical.
 */
export function AronDock() {
  const convId = useAronUiStore((s) => s.dockConvId);
  const setConvId = useAronUiStore((s) => s.setDockConvId);
  const closeDock = useAronUiStore((s) => s.closeDock);
  const setDockMode = useAronUiStore((s) => s.setDockMode);

  const [showList, setShowList] = useState(false);

  const select = (id: string | null) => {
    setConvId(id);
    setShowList(false);
  };
  const { sending, handleSend, handleStop } = useAronChat(convId, select);
  // The dock is on screen, so no background badge — but a write ARON proposes
  // here still opens the approval gate.
  useAronTabSignals(convId, true);

  const conv = useAronConversationInfo(convId);
  // Świeża karta nie ma jeszcze rozmowy; otwarty stary wątek ma tytuł dociągnięty.
  const title = convId ? conv?.title || "Rozmowa" : "Nowa rozmowa";

  const headerBtn =
    "inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600 dark:text-gray-400 dark:hover:bg-gray-800";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 items-center gap-1.5 border-b border-gray-200 px-2 py-1.5 dark:border-gray-700">
        <button
          type="button"
          onClick={() => setShowList((v) => !v)}
          title={showList ? "Wróć do rozmowy" : "Lista rozmów"}
          className={headerBtn}
          aria-pressed={showList}
        >
          {showList ? <Bot size={15} /> : <List size={15} />}
        </button>
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <Bot size={14} className="shrink-0 text-red-500" />
          <span className="truncate text-sm font-medium text-gray-700 dark:text-gray-200">
            {showList ? "Rozmowy ARON" : title}
          </span>
        </span>
        <button
          type="button"
          onClick={() => select(null)}
          title="Nowa rozmowa"
          className={headerBtn}
        >
          <MessageSquarePlus size={15} />
        </button>
        <button
          type="button"
          onClick={() => setDockMode("float")}
          title="Odepnij — pływające okno nad stroną, z własnymi kartami (duży ekran)"
          className={`${headerBtn} hidden md:inline-flex`}
        >
          <PictureInPicture2 size={15} />
        </button>
        <Link
          to={convId ? `/aron?c=${convId}` : "/aron"}
          onClick={closeDock}
          title="Otwórz na pełnej stronie"
          className={headerBtn}
        >
          <Maximize2 size={14} />
        </Link>
        <button
          type="button"
          onClick={closeDock}
          title="Zamknij"
          className={headerBtn}
        >
          <X size={16} />
        </button>
      </header>
      <div className="min-h-0 flex-1">
        {showList ? (
          <AronConversationList
            selectedId={convId}
            onSelect={select}
            onNew={() => select(null)}
          />
        ) : (
          <AronChatView
            key={convId ?? "new"}
            conversationId={convId}
            sending={sending}
            onSend={handleSend}
            onStop={handleStop}
          />
        )}
      </div>
    </div>
  );
}
